import React, { useState } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const numericArray = (values) =>
  Array.isArray(values) ? values.map((value) => toFiniteNumber(value)) : [];

const PROJECTION_YEAR_FLOOR = 2024;

const getProjectionYearValues = (explicitProjectionYears = []) =>
  numericArray(explicitProjectionYears).filter((year) => year !== null);

const getProjectionCount = (rawYears, data, explicitProjectionYears = []) => {
  const years = Array.isArray(rawYears) ? rawYears : [];
  const numericYears = years.map((year) => toFiniteNumber(year));
  const explicitYears = getProjectionYearValues(explicitProjectionYears);
  const firstExplicitProjectionYear = explicitYears.find((year) => year >= PROJECTION_YEAR_FLOOR);
  if (firstExplicitProjectionYear !== undefined) {
    const explicitStartIndex = numericYears.findIndex((year) => year === firstExplicitProjectionYear);
    if (explicitStartIndex >= 0) return years.length - explicitStartIndex;
    return Math.min(explicitYears.length, years.length);
  }

  const projectionStartIndex = numericYears.findIndex((year) => year !== null && year >= PROJECTION_YEAR_FLOOR);
  if (projectionStartIndex >= 0) return years.length - projectionStartIndex;

  const parsedProjectionYears =
    Number(data?.n_projection_years) ||
    Number(data?.n_proj_years) ||
    Number(data?.n_forecast_years) ||
    Number(data?.n_future_years) ||
    Number(data?.projection_years_count);
  if (Number.isFinite(parsedProjectionYears) && parsedProjectionYears > 0) {
    return Math.min(parsedProjectionYears, years.length);
  }

  return 0;
};

const getHistoricalCount = (rawYears, data, nBaseYears, explicitProjectionYears = []) => {
  const years = Array.isArray(rawYears) ? rawYears : [];
  const projectionCount = getProjectionCount(years, data, explicitProjectionYears);
  if (projectionCount > 0) return Math.max(0, years.length - projectionCount);

  return Math.max(0, Math.min(nBaseYears, years.length));
};

const normalizeProjectionYears = (rawYears, data, nBaseYears, explicitProjectionYears = []) => {
  const years = Array.isArray(rawYears) ? rawYears : [];
  const numericYears = years.map((year) => toFiniteNumber(year));
  const historicalCount = getHistoricalCount(years, data, nBaseYears, explicitProjectionYears);
  const baseEndIndex = Math.max(0, Math.min(historicalCount - 1, years.length - 1));
  const lastHistoricalYear = numericYears[baseEndIndex];
  const projectionYears = getProjectionYearValues(explicitProjectionYears);

  if (lastHistoricalYear === null) return years;

  return years.map((year, idx) => {
    if (idx < historicalCount) return year;

    const projectionIndex = idx - historicalCount;
    const explicitYear = projectionYears[projectionIndex];
    if (explicitYear !== null && explicitYear > lastHistoricalYear) {
      return explicitYear;
    }

    return lastHistoricalYear + projectionIndex + 1;
  });
};

const formatNumber = (value, options) => {
  const numberValue = toFiniteNumber(value);
  return numberValue === null ? 'N/A' : numberValue.toLocaleString(undefined, options);
};

const formatFixed = (value, digits) => {
  const numberValue = toFiniteNumber(value);
  return numberValue === null ? 'N/A' : numberValue.toFixed(digits);
};

const formatPercentFixed = (value, digits) => {
  const numberValue = toFiniteNumber(value);
  return numberValue === null ? 'N/A' : `${numberValue.toFixed(digits)}%`;
};

const WHAMProjectionCharts = ({ stockId, stockUuid }) => {
  const [activeChart, setActiveChart] = useState('ssb-f');
  const pageData = usePageDataContextOptional();
  const data = pageData?.data?.whamProjectionData;

  if (!stockId && !stockUuid) return <div className="p-4">No stock selected</div>;
  if (!data) return <div className="p-4">Run a WHAM projection to view outputs.</div>;
  if (!Array.isArray(data.years) || !data.timeseries || typeof data.timeseries !== 'object') {
    return <div className="p-4">WHAM projection output is missing chart-ready time series data.</div>;
  }

  const parsedBaseYears = Number(data.n_base_years);
  const nBaseYears = Number.isFinite(parsedBaseYears) ? parsedBaseYears : data.years.length;
  // Navigation buttons
  const chartTypes = [
    { id: 'ssb-f', label: 'SSB & F Time Series' },
    { id: 'kobe', label: 'Kobe Plot' },
    { id: 'selectivity', label: 'Selectivity' },
    { id: 'naa-stacked', label: 'Numbers at Age' },
  ];

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div 
        className="flex" 
        style={{ 
          marginBottom: '6px',
          borderBottom: '2px solid #e5e7eb',
          position: 'relative'
        }}
      >
        {chartTypes.map((chart, index) => (
          <button
            key={chart.id}
            onClick={() => setActiveChart(chart.id)}
            style={{
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: activeChart === chart.id ? '600' : '500',
              color: activeChart === chart.id ? '#2563eb' : '#6b7280',
              backgroundColor: activeChart === chart.id ? 'white' : 'transparent',
              border: 'none',
              borderBottom: activeChart === chart.id ? '3px solid #2563eb' : '3px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'relative',
              marginBottom: '-2px',
              borderTopLeftRadius: '6px',
              borderTopRightRadius: '6px',
              borderLeft: index === 0 ? '1px solid #e5e7eb' : 'none',
              borderRight: '1px solid #e5e7eb',
              borderTop: '1px solid #e5e7eb',
              zIndex: activeChart === chart.id ? 1 : 0
            }}
            onMouseEnter={(e) => {
              if (activeChart !== chart.id) {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.color = '#374151';
              }
            }}
            onMouseLeave={(e) => {
              if (activeChart !== chart.id) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }
            }}
          >
            {chart.label}
          </button>
        ))}
      </div>

      {/* Charts Content */}
      <div 
        className="bg-white p-4 shadow"
        style={{
          border: '1px solid #e5e7eb',
          borderTop: 'none',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          minHeight: '400px'
        }}
      >
        {activeChart === 'ssb-f' && (
          <SSBFTimeSeries
            data={data}
            nBaseYears={nBaseYears}
            projectionYears={pageData?.data?.projectionYears}
          />
        )}
        {activeChart === 'kobe' && (
          <KobePlot
            data={data}
            nBaseYears={nBaseYears}
            projectionYears={pageData?.data?.projectionYears}
          />
        )}
        {activeChart === 'selectivity' && <SelectivityChart data={data} />}
        {activeChart === 'naa-stacked' && (
          <NumbersAtAgeStacked
            data={data}
            nBaseYears={nBaseYears}
            projectionYears={pageData?.data?.projectionYears}
          />
        )}
      </div>
    </div>
  );
};

// SSB and F Time Series Component
const SSBFTimeSeries = ({ data, nBaseYears, projectionYears }) => {
  const years = normalizeProjectionYears(data.years, data, nBaseYears, projectionYears);
  const historicalCount = getHistoricalCount(data.years, data, nBaseYears, projectionYears);
  const ssbSeries = numericArray(data.timeseries?.ssb);
  const fSeries = numericArray(data.timeseries?.f);
  const projectionStartYear = years[historicalCount];

  // Prepare data for SSB chart
  const ssbData = years.map((year, idx) => ({
    year,
    historicalSsb: idx < historicalCount ? ssbSeries[idx] : null,
    projectedSsb: idx >= historicalCount ? ssbSeries[idx] : null,
  }));

  // Prepare data for F chart
  const fData = years.map((year, idx) => ({
    year,
    historicalF: idx < historicalCount ? fSeries[idx] : null,
    projectedF: idx >= historicalCount ? fSeries[idx] : null,
  }));

  const ssbTarget = toFiniteNumber(data.reference_points?.ssb_fxspr);
  const fTarget = toFiniteNumber(data.reference_points?.f_xspr);

  return (
    <div className="space-y-6">
      {/* SSB Chart */}
      <div>
        <h3 className="text-lg font-bold mb-2">Spawning Stock Biomass (SSB)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={ssbData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="year" />
            <YAxis 
              label={{ value: 'SSB (mt)', angle: -90, position: 'insideLeft' }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip 
              formatter={(value) => formatNumber(value, { maximumFractionDigits: 0 })}
            />
            <Legend />
            
            {/* Reference line for target */}
            {ssbTarget !== null && (
              <ReferenceLine 
                y={ssbTarget} 
                stroke="green" 
                strokeDasharray="5 5" 
                label={`SSB@F50% = ${ssbTarget.toLocaleString()}`}
                strokeWidth={2}
              />
            )}
            
            {/* Vertical line at projection start */}
            {projectionStartYear !== undefined && (
              <ReferenceLine
                x={projectionStartYear}
                stroke="red"
                strokeDasharray="3 3"
                strokeWidth={2}
                opacity={0.5}
              />
            )}
            
            {/* Historical line */}
            <Line
              dataKey="historicalSsb"
              stroke="#2E86AB"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="Historical"
              connectNulls={false}
            />
            
            {/* Projection line */}
            <Line
              dataKey="projectedSsb"
              stroke="#A23B72"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4, fill: "#A23B72" }}
              name="Projected"
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* F Chart */}
      <div>
        <h3 className="text-lg font-bold mb-2">Fishing Mortality (F)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={fData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="year" />
            <YAxis 
              label={{ value: 'F', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip formatter={(value) => formatFixed(value, 4)} />
            <Legend />
            
            {/* Reference line for target */}
            {fTarget !== null && (
              <ReferenceLine 
                y={fTarget} 
                stroke="green" 
                strokeDasharray="5 5" 
                label={`F50% = ${fTarget.toFixed(3)}`}
                strokeWidth={2}
              />
            )}
            
            {/* Vertical line at projection start */}
            {projectionStartYear !== undefined && (
              <ReferenceLine
                x={projectionStartYear}
                stroke="red"
                strokeDasharray="3 3"
                strokeWidth={2}
                opacity={0.5}
              />
            )}
            
            {/* Historical line */}
            <Line
              dataKey="historicalF"
              stroke="#2E86AB"
              strokeWidth={2}
              dot={{ r: 3 }}
              name="Historical"
              connectNulls={false}
            />
            
            {/* Projection line */}
            <Line
              dataKey="projectedF"
              stroke="#A23B72"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4, fill: "#A23B72" }}
              name="Projected"
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Kobe Plot Component
const KobePlot = ({ data, nBaseYears, projectionYears }) => {
  const years = normalizeProjectionYears(data.years, data, nBaseYears, projectionYears);
  const historicalCount = getHistoricalCount(data.years, data, nBaseYears, projectionYears);
  const ssbRelativeSeries = numericArray(data.relative_status?.ssb_relative);
  const fRelativeSeries = numericArray(data.relative_status?.f_relative);
  const kobeData = years.map((year, idx) => ({
    year,
    ssbRelative: ssbRelativeSeries[idx],
    fRelative: fRelativeSeries[idx],
    isHistorical: idx < historicalCount,
    isProjection: idx >= historicalCount,
    isStart: idx === 0,
    isCurrent: idx === historicalCount - 1,
    isFinal: idx === years.length - 1,
  }));

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (payload.isStart) {
      return <circle cx={cx} cy={cy} r={8} fill="green" stroke="black" strokeWidth={1} />;
    }
    if (payload.isCurrent) {
      return <path d={`M${cx},${cy-10} L${cx+8},${cy+10} L${cx-8},${cy+10} Z`} fill="blue" stroke="black" strokeWidth={1} />;
    }
    if (payload.isFinal) {
      return (
        <g>
          <path d={`M${cx},${cy-10} L${cx+9},${cy+3} L${cx+5.5},${cy+10} L${cx},${cy+5} L${cx-5.5},${cy+10} L${cx-9},${cy+3} Z`} 
                fill="red" stroke="black" strokeWidth={1} />
        </g>
      );
    }
    
    return payload.isHistorical ? 
      <circle cx={cx} cy={cy} r={3} fill="#2E86AB" /> :
      <rect x={cx-3} y={cy-3} width={6} height={6} fill="#A23B72" />;
  };

  return (
    <div>
      <h3 className="text-lg font-bold mb-2">Kobe Plot - Stock Status Relative to Benchmarks</h3>
      <ResponsiveContainer width="100%" height={600}>
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            type="number" 
            dataKey="ssbRelative" 
            domain={[0, 3]}
            label={{ value: 'SSB / SSB@F50%', position: 'bottom' }}
          />
          <YAxis 
            type="number" 
            dataKey="fRelative"
            domain={[0, 2.5]}
            label={{ value: 'F / F50%', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip 
            formatter={(value) => formatFixed(value, 2)}
            labelFormatter={(label) => `Year: ${label}`}
          />
          <Legend />
          
          {/* Reference lines */}
          <ReferenceLine x={1} stroke="black" strokeWidth={2} strokeDasharray="5 5" />
          <ReferenceLine y={1} stroke="black" strokeWidth={2} strokeDasharray="5 5" />
          
          {/* Quadrant coloring - using ReferenceArea */}
          {/* Red: Overfished & Overfishing (SSB < 1, F > 1) */}
          <ReferenceArea x1={0} x2={1} y1={1} y2={2.5} fill="red" fillOpacity={0.2} />
          
          {/* Yellow: Overfished only (SSB < 1, F < 1) */}
          <ReferenceArea x1={0} x2={1} y1={0} y2={1} fill="yellow" fillOpacity={0.2} />
          
          {/* Yellow: Overfishing only (SSB > 1, F > 1) */}
          <ReferenceArea x1={1} x2={3} y1={1} y2={2.5} fill="yellow" fillOpacity={0.2} />
          
          {/* Green: Healthy (SSB > 1, F < 1) */}
          <ReferenceArea x1={1} x2={3} y1={0} y2={1} fill="green" fillOpacity={0.2} />
          
          {/* Historical trajectory */}
          <Scatter
            data={kobeData.filter(d => d.isHistorical)}
            fill="#2E86AB"
            line={{ stroke: '#2E86AB', strokeWidth: 2 }}
            shape={<CustomDot />}
            name="Historical"
          />
          
          {/* Projected trajectory */}
          <Scatter
            data={kobeData.filter(d => d.isProjection)}
            fill="#A23B72"
            line={{ stroke: '#A23B72', strokeWidth: 2, strokeDasharray: '5 5' }}
            shape={<CustomDot />}
            name="Projected"
          />
        </ScatterChart>
      </ResponsiveContainer>
      
      <div className="mt-4 text-sm">
        <p><span className="inline-block w-4 h-4 bg-green-500 rounded-full mr-2"></span>Start Year ({years[0]})</p>
        <p><span className="inline-block w-4 h-4 bg-blue-500 mr-2" style={{clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)'}}></span>Current Year ({years[historicalCount - 1]})</p>
        <p><span className="inline-block w-4 h-4 bg-red-500 mr-2" style={{clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'}}></span>Final Year ({years[years.length - 1]})</p>
      </div>
    </div>
  );
};

// Selectivity Chart Component
const SelectivityChart = ({ data }) => {
  const ages = data.ages ?? [];
  const fleetSelectivity = numericArray(data.age_data?.selectivity?.fleet?.[0]);
  const fallSurveySelectivity = numericArray(data.age_data?.selectivity?.fall_survey?.[0]);
  const springSurveySelectivity = numericArray(data.age_data?.selectivity?.spring_survey?.[0]);
  const selectivityData = ages.map((age, idx) => ({
    age,
    fleet: fleetSelectivity[idx],
    fallSurvey: fallSurveySelectivity[idx],
    springSurvey: springSurveySelectivity[idx],
  }));

  return (
    <div>
      <h3 className="text-lg font-bold mb-2">Selectivity at Age</h3>
      <ResponsiveContainer width="100%" height={500}>
        <LineChart data={selectivityData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis 
            dataKey="age"
            label={{ value: 'Age', position: 'bottom' }}
          />
          <YAxis 
            domain={[0, 1.1]}
            label={{ value: 'Selectivity', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip formatter={(value) => formatFixed(value, 3)} />
          <Legend />
          
          <Line
            dataKey="fleet"
            stroke="#2E86AB"
            strokeWidth={2.5}
            dot={{ r: 4 }}
            name="Fleet (Trawl)"
          />
          <Line
            dataKey="fallSurvey"
            stroke="#A23B72"
            strokeWidth={2.5}
            dot={{ r: 4 }}
            name="Fall Survey"
          />
          <Line
            dataKey="springSurvey"
            stroke="#F18F01"
            strokeWidth={2.5}
            dot={{ r: 4 }}
            name="Spring Survey"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Numbers at Age Stacked Area Component
const NumbersAtAgeStacked = ({ data, nBaseYears, projectionYears }) => {
  const years = normalizeProjectionYears(data.years, data, nBaseYears, projectionYears);
  const historicalCount = getHistoricalCount(data.years, data, nBaseYears, projectionYears);
  const ages = data.ages ?? [];
  const naaRows = Array.isArray(data.age_data?.naa) ? data.age_data.naa : [];

  // Calculate proportions
  const naaProportions = years.map((year, yearIdx) => {
    const yearData = { year };
    const naaValues = numericArray(naaRows[yearIdx]);
    const total = naaValues.reduce((sum, val) => sum + (val ?? 0), 0);
    
    ages.forEach((age, ageIdx) => {
      const value = naaValues[ageIdx] ?? 0;
      yearData[`age_${age}`] = total > 0 ? (value / total) * 100 : null;
    });
    
    return yearData;
  });

  // Generate color palette (blue to red gradient)
  const generateColors = (numColors) => {
    if (numColors <= 0) return [];
    if (numColors === 1) return ['rgb(51, 127, 204)'];

    const colors = [];
    for (let i = 0; i < numColors; i++) {
      const frac = i / (numColors - 1);
      let r, g, b;
      
      if (frac < 0.25) {
        // Blue to cyan
        r = Math.floor(51 + frac * 305);
        g = Math.floor(127 + frac * 510);
        b = Math.floor(204 + frac * 204);
      } else if (frac < 0.5) {
        // Cyan to green
        r = Math.floor(127 - (frac - 0.25) * 510);
        g = 229;
        b = Math.floor(229 - (frac - 0.25) * 382);
      } else if (frac < 0.75) {
        // Green to yellow
        r = Math.floor(127 + (frac - 0.5) * 510);
        g = 229;
        b = Math.floor(102 - (frac - 0.5) * 408);
      } else {
        // Yellow to red
        r = 255;
        g = Math.floor(229 - (frac - 0.75) * 918);
        b = 0;
      }
      
      colors.push(`rgb(${Math.min(r, 255)}, ${Math.min(g, 255)}, ${Math.max(b, 0)})`);
    }
    return colors;
  };

  const colors = generateColors(ages.length);

  return (
    <div>
      <h3 className="text-lg font-bold mb-2">Numbers at Age (Proportion)</h3>
      <ResponsiveContainer width="100%" height={600}>
        <AreaChart data={naaProportions} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis 
            dataKey="year"
            label={{ value: 'Year', position: 'bottom' }}
          />
          <YAxis 
            domain={[0, 100]}
            label={{ value: 'Proportion at age (%)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip formatter={(value) => formatPercentFixed(value, 1)} />
          
          {/* Projection start line */}
          <ReferenceLine 
            x={years[historicalCount]} 
            stroke="black" 
            strokeWidth={2}
            strokeDasharray="5 5"
          />
          
          {/* Stack areas for each age */}
          {ages.map((age, idx) => (
            <Area
              key={age}
              type="monotone"
              dataKey={`age_${age}`}
              stackId="1"
              stroke="black"
              strokeWidth={0.3}
              fill={colors[idx]}
              name={`Age ${age}`}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      
      <div className="mt-2 text-sm text-gray-600">
        <p>Black dashed line indicates start of projection period (Year {years[historicalCount]})</p>
      </div>
    </div>
  );
};

export default WHAMProjectionCharts;
